import { useState, useCallback, useRef, useEffect } from 'react';
import { startGeneration, startFromTemplate, startFromTemplatePersonalized, getHistory, getTemplates, renameTemplate } from '@/api/client';
import type { HistoryRun, TemplateInfo } from '@/api/client';

interface IngestPageProps {
  readonly onRunStarted: (runId: string) => void;
  readonly onViewHistory: (folderName: string) => void;
}

export function IngestPage({ onRunStarted, onViewHistory }: IngestPageProps) {
  const [jsonText, setJsonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryRun[]>([]);
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getHistory()
      .then(setHistory)
      .catch(() => {});
    getTemplates()
      .then(setTemplates)
      .catch(() => {});
  }, []);

  const handleRename = useCallback(async (id: string, name: string) => {
    if (!name.trim()) return;
    try {
      await renameTemplate(id, name.trim());
      setTemplates(prev => prev.map(t => (t.id === id ? { ...t, name: name.trim() } : t)));
    } catch (err) {
      setError(`Rename failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    setRenamingId(null);
  }, []);

  const handleSubmit = useCallback(
    async (raw: string) => {
      if (!raw.trim()) {
        setError('Please provide event JSON');
        return;
      }

      setSubmitting(true);
      setError(null);

      try {
        const parsed = JSON.parse(raw);
        const { runId } = selectedTemplate
          ? await startFromTemplate(selectedTemplate, parsed)
          : await startGeneration(parsed);
        onRunStarted(runId);
      } catch (err) {
        setError(err instanceof SyntaxError ? 'Invalid JSON' : String(err));
        setSubmitting(false);
      }
    },
    [onRunStarted, selectedTemplate],
  );

  const handleSubmitPersonalized = useCallback(
    async (raw: string) => {
      if (!raw.trim() || !selectedTemplate) return;

      setSubmitting(true);
      setError(null);

      try {
        const parsed = JSON.parse(raw);
        const { runId } = await startFromTemplatePersonalized(selectedTemplate, parsed);
        onRunStarted(runId);
      } catch (err) {
        setError(err instanceof SyntaxError ? 'Invalid JSON' : String(err));
        setSubmitting(false);
      }
    },
    [onRunStarted, selectedTemplate],
  );

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      const text = event.target?.result;
      if (typeof text === 'string') setJsonText(text);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      const text = event.target?.result;
      if (typeof text === 'string') setJsonText(text);
    };
    reader.readAsText(file);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>Event Website Generator</h1>
        <p style={styles.subtitle}>Upload or paste your exported event JSON to generate a website.</p>

        {/* Template selection */}
        {templates.length > 0 && (
          <div style={styles.templateSection}>
            <label style={styles.label}>Start from a template (optional):</label>
            <div style={styles.templateList}>
              <button
                style={selectedTemplate === null ? styles.templateItemSelected : styles.templateItem}
                onClick={() => setSelectedTemplate(null)}
              >
                Generate from scratch
              </button>
              {templates.map(t => (
                <div
                  key={t.id}
                  style={selectedTemplate === t.id ? styles.templateItemSelected : styles.templateItem}
                  onClick={() => setSelectedTemplate(t.id)}
                >
                  {renamingId === t.id ? (
                    <div
                      style={styles.renameRow}
                      onClick={e => e.stopPropagation()}
                    >
                      <input
                        style={styles.renameInput}
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRename(t.id, renameValue);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        autoFocus
                      />
                      <button
                        style={styles.renameSave}
                        onClick={() => handleRename(t.id, renameValue)}
                      >
                        Save
                      </button>
                      <button
                        style={styles.renameCancel}
                        onClick={() => setRenamingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={styles.templateRow}>
                      <span style={styles.templateName}>{t.name}</span>
                      <button
                        style={styles.renameButton}
                        onClick={e => {
                          e.stopPropagation();
                          setRenamingId(t.id);
                          setRenameValue(t.name);
                        }}
                      >
                        Rename
                      </button>
                    </div>
                  )}
                  {t.description && <span style={styles.templateDesc}>{t.description}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          style={styles.dropZone}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <p style={styles.dropText}>Drag & drop a JSON file here</p>
          <p style={styles.orText}>or</p>
          <button
            style={styles.uploadButton}
            onClick={() => fileInputRef.current?.click()}
          >
            Choose File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            style={styles.hiddenInput}
          />
        </div>

        <div style={styles.pasteSection}>
          <label
            htmlFor="json-input"
            style={styles.label}
          >
            Or paste JSON directly:
          </label>
          <textarea
            id="json-input"
            style={styles.textarea}
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
            placeholder='{"id": "...", "meta": {...}, "schedule": {...}, ...}'
            rows={12}
          />
          <div style={styles.buttonRow}>
            {selectedTemplate && (
              <button
                style={{ ...styles.personalizeButton, ...(submitting ? styles.generateButtonDisabled : {}) }}
                onClick={() => handleSubmitPersonalized(jsonText)}
                disabled={submitting}
              >
                {submitting ? 'Starting...' : 'Personalize Template'}
              </button>
            )}
            <button
              style={{ ...styles.generateButton, ...(submitting ? styles.generateButtonDisabled : {}) }}
              onClick={() => handleSubmit(jsonText)}
              disabled={submitting}
            >
              {submitting ? 'Starting...' : selectedTemplate ? 'Apply Template' : 'Generate Website'}
            </button>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {history.length > 0 && (
          <div style={styles.historySection}>
            <h2 style={styles.historyTitle}>Previous Generations</h2>
            <div style={styles.historyList}>
              {history.map(run => (
                <button
                  key={run.folderName}
                  style={styles.historyItem}
                  onClick={() => onViewHistory(run.folderName)}
                >
                  <span style={styles.historyName}>{run.folderName}</span>
                  {run.timestamp && (
                    <span style={styles.historyMeta}>
                      {new Date(run.timestamp).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0a0a0a',
    color: '#e0e0e0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: 24,
  },
  content: { maxWidth: 600, width: '100%' },
  title: { fontSize: 28, fontWeight: 600, marginBottom: 8, color: '#ffffff' },
  subtitle: { fontSize: 14, color: '#888', marginBottom: 32 },
  templateSection: { marginBottom: 24 },
  templateList: { display: 'flex', flexDirection: 'column' as const, gap: 6, marginTop: 8 },
  templateItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    padding: '10px 14px',
    backgroundColor: '#111',
    border: '1px solid #222',
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'left' as const,
    color: '#e0e0e0',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box' as const,
    overflow: 'hidden',
  },
  templateItemSelected: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    padding: '10px 14px',
    backgroundColor: '#1a1a2e',
    border: '1px solid #1a65d6',
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'left' as const,
    color: '#e0e0e0',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box' as const,
    overflow: 'hidden',
  },
  templateRow: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 },
  templateName: {
    fontWeight: 500,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  templateDesc: { fontSize: 12, color: '#666' },
  renameButton: {
    padding: '2px 8px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #333',
    borderRadius: 3,
    cursor: 'pointer',
    fontSize: 11,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  },
  renameRow: { display: 'flex', gap: 6, alignItems: 'center', width: '100%' },
  renameInput: {
    flex: 1,
    padding: '4px 8px',
    backgroundColor: '#0a0a0a',
    color: '#e0e0e0',
    border: '1px solid #444',
    borderRadius: 3,
    fontSize: 13,
    outline: 'none',
  },
  renameSave: {
    padding: '3px 10px',
    backgroundColor: '#1a65d6',
    color: '#fff',
    border: 'none',
    borderRadius: 3,
    cursor: 'pointer',
    fontSize: 12,
  },
  renameCancel: {
    padding: '3px 10px',
    backgroundColor: 'transparent',
    color: '#888',
    border: '1px solid #333',
    borderRadius: 3,
    cursor: 'pointer',
    fontSize: 12,
  },
  label: { fontSize: 13, color: '#888' },
  dropZone: {
    border: '2px dashed #333',
    borderRadius: 12,
    padding: 32,
    textAlign: 'center' as const,
    marginBottom: 24,
  },
  dropText: { fontSize: 14, color: '#888', margin: 0 },
  orText: { fontSize: 12, color: '#555', margin: '12px 0' },
  uploadButton: {
    padding: '8px 20px',
    backgroundColor: '#222',
    color: '#e0e0e0',
    border: '1px solid #333',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
  },
  hiddenInput: { display: 'none' },
  pasteSection: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  textarea: {
    width: '100%',
    padding: 12,
    backgroundColor: '#111',
    color: '#e0e0e0',
    border: '1px solid #333',
    borderRadius: 8,
    fontFamily: 'monospace',
    fontSize: 12,
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  buttonRow: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  generateButton: {
    padding: '10px 24px',
    backgroundColor: '#fff',
    color: '#000',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
  personalizeButton: {
    padding: '10px 24px',
    backgroundColor: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
  generateButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  error: {
    marginTop: 16,
    padding: 12,
    backgroundColor: 'rgba(255, 60, 60, 0.1)',
    border: '1px solid rgba(255, 60, 60, 0.3)',
    borderRadius: 8,
    color: '#ff6b6b',
    fontSize: 13,
  },
  historySection: { marginTop: 48, borderTop: '1px solid #222', paddingTop: 32 },
  historyTitle: { fontSize: 16, fontWeight: 500, color: '#ccc', marginBottom: 16 },
  historyList: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  historyItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#111',
    border: '1px solid #222',
    borderRadius: 8,
    cursor: 'pointer',
    textAlign: 'left' as const,
    color: '#e0e0e0',
    fontSize: 13,
    width: '100%',
    transition: 'border-color 0.15s',
  },
  historyName: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  historyMeta: { color: '#666', fontSize: 12, marginLeft: 12, flexShrink: 0 },
};
